import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';

interface EmailSettings {
  sendgridApiKey: string;
  fromEmail: string;
  fromName: string;
  notificationEmails: string[];
  enabled: boolean;
}

interface EmailSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EmailSettingsModal: React.FC<EmailSettingsModalProps> = ({ open, onOpenChange }) => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<EmailSettings>({
    sendgridApiKey: '',
    fromEmail: 'dev@stitchdesignco.com',
    fromName: 'SDCO Professional Pathways',
    notificationEmails: [],
    enabled: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [apiKeyMasked, setApiKeyMasked] = useState(false);

  useEffect(() => {
    if (open) {
      loadSettings();
    }
  }, [open, currentUser]);

  const loadSettings = async () => {
    try {
      if (!currentUser) return;
      
      setLoading(true);
      setMessage(null);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const settingsRef = doc(db, 'settings', 'emailNotifications');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          sendgridApiKey: data.sendgridApiKey ? '****' : '',
          fromEmail: data.fromEmail || 'dev@stitchdesignco.com',
          fromName: data.fromName || 'SDCO Professional Pathways',
          notificationEmails: data.notificationEmails || [],
          enabled: data.enabled || false
        });
        setApiKeyMasked(!!data.sendgridApiKey);
      } else {
        // Use defaults
        setSettings({
          sendgridApiKey: '',
          fromEmail: 'dev@stitchdesignco.com',
          fromName: 'SDCO Professional Pathways',
          notificationEmails: [],
          enabled: false
        });
        setApiKeyMasked(false);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      if (!currentUser) return;
      
      setSaving(true);
      setMessage(null);
      
      const { doc, setDoc, getDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const settingsRef = doc(db, 'settings', 'emailNotifications');
      
      // Build the update data
      const updateData: any = {
        fromEmail: settings.fromEmail || 'dev@stitchdesignco.com',
        fromName: settings.fromName || 'SDCO Professional Pathways',
        notificationEmails: settings.notificationEmails || [],
        enabled: settings.enabled,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.email
      };
      
      // Only update API key if a new one is provided (not masked)
      if (settings.sendgridApiKey && settings.sendgridApiKey !== '****') {
        updateData.sendgridApiKey = settings.sendgridApiKey;
      } else if (apiKeyMasked) {
        // Preserve existing API key - fetch it first
        const existingDoc = await getDoc(settingsRef);
        if (existingDoc.exists()) {
          updateData.sendgridApiKey = existingDoc.data().sendgridApiKey || '';
        }
      }
      
      await setDoc(settingsRef, updateData, { merge: true });
      
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
      
      // Update the masked state if we saved a new key
      if (settings.sendgridApiKey && settings.sendgridApiKey !== '****') {
        setSettings(prev => ({ ...prev, sendgridApiKey: '****' }));
        setApiKeyMasked(true);
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof EmailSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    // If user is typing in the API key field and it was masked, clear the mask
    if (field === 'sendgridApiKey' && apiKeyMasked && value !== '****') {
      setApiKeyMasked(false);
    }
  };

  const handleApiKeyFocus = () => {
    // When the user focuses on the masked API key field, clear it for new entry
    if (settings.sendgridApiKey === '****') {
      setSettings(prev => ({ ...prev, sendgridApiKey: '' }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Notification Settings
          </DialogTitle>
          <DialogDescription>
            Configure email notifications for peer review and engagement pulse submissions.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Status message */}
            {message && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                message.type === 'success' 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            {/* Enable toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={settings.enabled}
                onCheckedChange={(checked) => handleInputChange('enabled', checked)}
              />
              <Label htmlFor="enabled" className="cursor-pointer">
                Enable email notifications
              </Label>
            </div>

            {/* SendGrid API Key */}
            <div className="space-y-2">
              <Label htmlFor="apiKey">SendGrid API Key</Label>
              <Input
                id="apiKey"
                type="password"
                value={settings.sendgridApiKey}
                onChange={(e) => handleInputChange('sendgridApiKey', e.target.value)}
                onFocus={handleApiKeyFocus}
                placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <p className="text-xs text-muted-foreground">
                {apiKeyMasked ? (
                  <span className="text-green-600">✓ API key is configured. Enter a new key to replace it.</span>
                ) : (
                  <>
                    Get your API key from{' '}
                    <a 
                      href="https://app.sendgrid.com/settings/api_keys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      SendGrid Dashboard
                    </a>
                  </>
                )}
              </p>
            </div>

            {/* From Email */}
            <div className="space-y-2">
              <Label htmlFor="fromEmail">From Email</Label>
              <Input
                id="fromEmail"
                type="email"
                value={settings.fromEmail}
                onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                placeholder="dev@stitchdesignco.com"
              />
              <p className="text-xs text-muted-foreground">
                Must be verified in SendGrid
              </p>
            </div>

            {/* From Name */}
            <div className="space-y-2">
              <Label htmlFor="fromName">From Name</Label>
              <Input
                id="fromName"
                type="text"
                value={settings.fromName}
                onChange={(e) => handleInputChange('fromName', e.target.value)}
                placeholder="SDCO Professional Pathways"
              />
            </div>

            {/* Notification Recipients — now routed automatically */}
            <div className="space-y-2">
              <Label>Notification Recipients</Label>
              <div className="bg-muted/50 border rounded-lg p-3 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Recipients are now determined automatically — no manual list to maintain:
                </p>
                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                  <li>
                    <strong>Engagement pulse checks</strong> go to the employee who completed
                    the survey and their manager(s){' '}
                    <span className="text-muted-foreground/80">(plus any Super HR users)</span>.
                  </li>
                  <li>
                    <strong>Peer reviews</strong> go to the reviewee's manager(s). Anonymous
                    reviewers stay anonymous.
                  </li>
                </ul>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> After saving, deploy the Firebase functions to enable email notifications:
              </p>
              <code className="text-xs bg-blue-100 px-1 rounded mt-1 block">
                cd functions && firebase deploy --only functions
              </code>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={saveSettings}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailSettingsModal;
